/**
 * auth-modals.ts - Modal management for authentication and account features
 */

import type { AuthModalMode } from '../../types/auth';
import { getCurrentUser } from './auth-state';

/**
 * Open authentication modal (login or register)
 * @param mode - Modal mode: 'login' or 'register'
 */
export function openAuthModal(mode: AuthModalMode = 'login'): void {
  const modal = document.getElementById('auth-modal');
  const loginForm = document.getElementById('login-form');
  const registerForm = document.getElementById('register-form');

  if (!modal || !loginForm || !registerForm) return;

  // Clear previous errors and success messages
  const loginError = document.getElementById('login-error');
  const registerError = document.getElementById('register-error');
  const registerSuccess = document.getElementById('register-success');

  if (loginError) loginError.textContent = '';
  if (registerError) registerError.textContent = '';
  if (registerSuccess) {
    registerSuccess.textContent = '';
    registerSuccess.style.display = 'none';
  }

  // Reset forms
  const loginUsername = document.getElementById('login-username') as HTMLInputElement | null;
  const loginPassword = document.getElementById('login-password') as HTMLInputElement | null;
  const registerUsername = document.getElementById('register-username') as HTMLInputElement | null;
  const registerEmail = document.getElementById('register-email') as HTMLInputElement | null;
  const registerPassword = document.getElementById('register-password') as HTMLInputElement | null;
  const registerPasswordConfirm = document.getElementById('register-password-confirm') as HTMLInputElement | null;

  if (loginUsername) loginUsername.value = '';
  if (loginPassword) loginPassword.value = '';
  if (registerUsername) registerUsername.value = '';
  if (registerEmail) registerEmail.value = '';
  if (registerPassword) registerPassword.value = '';
  if (registerPasswordConfirm) registerPasswordConfirm.value = '';

  // Make sure form fields are visible (in case they were hidden after registration)
  const registerFormFields = document.getElementById('register-form-fields');
  if (registerFormFields) {
    registerFormFields.style.display = 'block';
  }

  // Reset wizard steps to step 1
  if (window.showRegisterStep1) {
    window.showRegisterStep1();
  }

  if (mode === 'login') {
    loginForm.style.display = 'block';
    registerForm.style.display = 'none';
  } else {
    loginForm.style.display = 'none';
    registerForm.style.display = 'block';
  }

  modal.style.display = 'block';
}

/**
 * Close authentication modal
 */
export function closeAuthModal(): void {
  const modal = document.getElementById('auth-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Switch to login view in auth modal
 */
export function switchToLogin(): void {
  openAuthModal('login');
}

/**
 * Switch to register view in auth modal
 */
export function switchToRegister(): void {
  openAuthModal('register');
}

/**
 * Open account settings modal
 */
export function openAccountModal(): void {
  const currentUser = getCurrentUser();
  if (!currentUser) return;

  const modal = document.getElementById('account-modal');
  if (!modal) return;

  // Update account info
  const accountUsername = document.getElementById('account-username');
  const accountEmail = document.getElementById('account-email');
  const accountCreated = document.getElementById('account-created');

  if (accountUsername) accountUsername.textContent = currentUser.username;
  if (accountEmail) accountEmail.textContent = currentUser.email;
  if (accountCreated) {
    const createdDate = new Date(currentUser.createdAt);
    accountCreated.textContent = createdDate.toLocaleDateString();
  }

  // Clear password change form
  const currentPassword = document.getElementById('current-password') as HTMLInputElement | null;
  const newPassword = document.getElementById('new-password') as HTMLInputElement | null;
  const newPasswordConfirm = document.getElementById('new-password-confirm') as HTMLInputElement | null;
  const passwordError = document.getElementById('password-error');
  const passwordSuccess = document.getElementById('password-success');

  if (currentPassword) currentPassword.value = '';
  if (newPassword) newPassword.value = '';
  if (newPasswordConfirm) newPasswordConfirm.value = '';
  if (passwordError) passwordError.textContent = '';
  if (passwordSuccess) passwordSuccess.textContent = '';

  // Load passkeys list
  if (window.loadPasskeysList) {
    window.loadPasskeysList();
  }

  modal.style.display = 'block';
}

/**
 * Close account settings modal
 */
export function closeAccountModal(): void {
  const modal = document.getElementById('account-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Open forgot password modal
 */
export function openForgotPasswordModal(): void {
  closeAuthModal();
  const modal = document.getElementById('forgot-password-modal');
  if (!modal) return;

  const forgotPasswordEmail = document.getElementById('forgot-password-email') as HTMLInputElement | null;
  const forgotPasswordError = document.getElementById('forgot-password-error');
  const forgotPasswordSuccess = document.getElementById('forgot-password-success');

  if (forgotPasswordEmail) forgotPasswordEmail.value = '';
  if (forgotPasswordError) forgotPasswordError.textContent = '';
  if (forgotPasswordSuccess) forgotPasswordSuccess.textContent = '';

  modal.style.display = 'block';
}

/**
 * Close forgot password modal
 */
export function closeForgotPasswordModal(): void {
  const modal = document.getElementById('forgot-password-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Open contact modal
 */
export function openContactModal(): void {
  const modal = document.getElementById('contact-modal');
  if (!modal) return;

  const currentUser = getCurrentUser();
  const contactName = document.getElementById('contact-name') as HTMLInputElement | null;
  const contactEmail = document.getElementById('contact-email') as HTMLInputElement | null;
  const contactSubject = document.getElementById('contact-subject') as HTMLInputElement | null;
  const contactMessage = document.getElementById('contact-message') as HTMLTextAreaElement | null;
  const contactError = document.getElementById('contact-error');
  const contactSuccess = document.getElementById('contact-success');

  // Pre-fill with user info if logged in
  if (currentUser) {
    if (contactName) contactName.value = currentUser.username;
    if (contactEmail) contactEmail.value = currentUser.email;
  } else {
    if (contactName) contactName.value = '';
    if (contactEmail) contactEmail.value = '';
  }

  if (contactSubject) contactSubject.value = '';
  if (contactMessage) contactMessage.value = '';
  if (contactError) contactError.textContent = '';
  if (contactSuccess) {
    contactSuccess.textContent = '';
    contactSuccess.style.display = 'none';
  }

  // Reset form visibility (in case it was hidden after successful send)
  const form = modal.querySelector('form');
  if (form) {
    const formGroups = form.querySelectorAll('.form-group, .modal-actions');
    formGroups.forEach((el) => {
      (el as HTMLElement).style.display = '';
    });
  }

  modal.style.display = 'block';
}

/**
 * Close contact modal
 */
export function closeContactModal(): void {
  const modal = document.getElementById('contact-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Open imprint modal
 */
export function openImprintModal(): void {
  const modal = document.getElementById('imprint-modal');
  if (modal) {
    modal.style.display = 'block';
  }
}

/**
 * Close imprint modal
 */
export function closeImprintModal(): void {
  const modal = document.getElementById('imprint-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Open privacy policy modal
 */
export function openPrivacyModal(): void {
  const modal = document.getElementById('privacy-modal');
  if (modal) {
    modal.style.display = 'block';
  }
}

/**
 * Close privacy policy modal
 */
export function closePrivacyModal(): void {
  const modal = document.getElementById('privacy-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Setup modal click-outside handlers
 */
export function setupModalHandlers(): void {
  window.onclick = function(event) {
    const authModal = document.getElementById('auth-modal');
    const accountModal = document.getElementById('account-modal');
    const forgotPasswordModal = document.getElementById('forgot-password-modal');
    const contactModal = document.getElementById('contact-modal');
    const imprintModal = document.getElementById('imprint-modal');
    const privacyModal = document.getElementById('privacy-modal');

    if (event.target === authModal) {
      closeAuthModal();
    }
    if (event.target === accountModal) {
      closeAccountModal();
    }
    if (event.target === forgotPasswordModal) {
      closeForgotPasswordModal();
    }
    if (event.target === contactModal) {
      closeContactModal();
    }
    if (event.target === imprintModal) {
      closeImprintModal();
    }
    if (event.target === privacyModal) {
      closePrivacyModal();
    }
  };
}

// Export to window for onclick handlers

window.openAuthModal = openAuthModal;
window.closeAuthModal = closeAuthModal;
window.switchToLogin = switchToLogin;
window.switchToRegister = switchToRegister;
window.openAccountModal = openAccountModal;
window.closeAccountModal = closeAccountModal;
window.openForgotPasswordModal = openForgotPasswordModal;
window.closeForgotPasswordModal = closeForgotPasswordModal;
window.openContactModal = openContactModal;
window.closeContactModal = closeContactModal;
window.openImprintModal = openImprintModal;
window.closeImprintModal = closeImprintModal;
window.openPrivacyModal = openPrivacyModal;
window.closePrivacyModal = closePrivacyModal;
